import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Phone, Mail, Users, Pencil, Stethoscope, AlertTriangle, Save, Pill, CheckCircle, XCircle, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import EditStudentProfileDialog from './EditStudentProfileDialog';
import { useUpdatePersonMutation } from './useUpdatePersonMutation';
import type { PersonFromStudentDetail } from './types';

interface StudentGeneralTabProps {
  /** PersonFromStudentDetail from query; uses Record<string, any> for test-mock compat */
  person: Record<string, any>;
  schoolSlug: string;
  onRefetch: () => void;
}

export function StudentGeneralTab({ person, schoolSlug, onRefetch }: StudentGeneralTabProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDialogMode, setEditDialogMode] = useState<'medical' | 'admin' | 'all'>('all');
  const [editedPersonData, setEditedPersonData] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [updatePerson] = useUpdatePersonMutation();

  // Check for changes
  const hasPersonChanges = useMemo(() => {
    return Object.keys(editedPersonData).length > 0;
  }, [editedPersonData]);

  // Handle person data change
  const handlePersonDataChange = useCallback((field: string, value: string) => {
    setEditedPersonData(prev => {
      const originalValue = person?.[field as keyof PersonFromStudentDetail];
      if (value === originalValue || (value === '' && !originalValue)) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [field]: value };
    });
  }, [person]);

  // Save person changes
  const handleSavePersonChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      await updatePerson({
        variables: {
          input: {
            personId: person.id,
            ...editedPersonData
          }
        }
      });
      setEditedPersonData({});
      onRefetch?.();
    } catch (error) {
      console.error('Failed to save person changes:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editedPersonData, person?.id, updatePerson, onRefetch]);

  // Cancel changes
  const handleCancelChanges = useCallback(() => {
    setEditedPersonData({});
  }, []);

  if (!person) return null;

  const studentProfile = person.studentProfile;
  const dateOfBirth = studentProfile?.dateOfBirth || person.dateOfBirth;
  const medicalInfo = studentProfile?.medicalInfo;
  const adminMetadata = studentProfile?.adminMetadata;

  // Get family from enrollment if available
  const family = person.studentEnrollmentForYear?.familyEnrollment?.family;

  const hasMedicalInfo = medicalInfo?.medicalCondition || medicalInfo?.specialNeeds ||
    medicalInfo?.otcMedicationPermission !== undefined;
  const hasAdminFlags = adminMetadata?.academicProbation || adminMetadata?.behavioralProbation;
  const hasAdminNotes = adminMetadata?.adminNotes;

  // Format OTC medications approved
  const formatOtcMedications = (meds: NonNullable<typeof medicalInfo>['otcMedicationsApproved']) => {
    if (!meds) return null;
    const approved: string[] = [];
    if (meds.acetaminophen) approved.push('Acetaminophen (Tylenol)');
    if (meds.ibuprofen) approved.push('Ibuprofen (Advil)');
    if (meds.coughDrops) approved.push('Cough Drops/Lozenges');
    if (meds.other) approved.push(meds.other);
    return approved.length > 0 ? approved.join(', ') : null;
  };

  return (
    <div className="space-y-6">
      {/* Floating save bar when there are changes */}
      {hasPersonChanges && (
        <Card className="sticky top-0 z-10 border-primary bg-primary/5">
          <CardContent className="flex items-center justify-between py-3">
            <span className="text-sm font-medium">You have unsaved changes</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancelChanges} disabled={isSaving}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSavePersonChanges} disabled={isSaving}>
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Person Identity Card - Editable */}
      <Card className={hasPersonChanges ? 'ring-2 ring-primary' : ''}>
        <CardHeader>
          <CardTitle className="text-lg">Identity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">First Name</Label>
                <Input
                  value={editedPersonData.firstName ?? person.firstName}
                  onChange={(e) => handlePersonDataChange('firstName', e.target.value)}
                  className="h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Last Name</Label>
                <Input
                  value={editedPersonData.lastName ?? person.lastName}
                  onChange={(e) => handlePersonDataChange('lastName', e.target.value)}
                  className="h-8"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <User className="h-3 w-3" />
                Gender
              </Label>
              <Select
                value={editedPersonData.gender ?? person.gender ?? ''}
                onValueChange={(value) => handlePersonDataChange('gender', value)}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select gender..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MALE">Male</SelectItem>
                  <SelectItem value="FEMALE">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email
              </Label>
              <Input
                type="email"
                value={editedPersonData.email ?? person.email ?? ''}
                onChange={(e) => handlePersonDataChange('email', e.target.value)}
                className="h-8"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Phone className="h-3 w-3" />
                Phone
              </Label>
              <Input
                type="tel"
                value={editedPersonData.phone ?? person.phone ?? ''}
                onChange={(e) => handlePersonDataChange('phone', e.target.value)}
                className="h-8"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Date of Birth
              </Label>
              <Input
                type="date"
                value={editedPersonData.dateOfBirth ?? dateOfBirth ?? ''}
                onChange={(e) => handlePersonDataChange('dateOfBirth', e.target.value)}
                className="h-8"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Medical Info Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            Medical Information
          </CardTitle>
          {studentProfile && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditDialogMode('medical');
                setIsEditDialogOpen(true);
              }}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {hasMedicalInfo ? (
            <div className="space-y-4">
              {/* Medical Condition */}
              {medicalInfo?.medicalCondition && (
                <div>
                  <p className="text-sm text-muted-foreground">Medical Condition</p>
                  <p className="font-medium">{medicalInfo.medicalCondition}</p>
                </div>
              )}

              {/* OTC Medication Permission */}
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Pill className="h-3 w-3" />
                  OTC Medication Permission
                </p>
                {medicalInfo?.otcMedicationPermission === true ? (
                  <div className="mt-1">
                    <p className="font-medium flex items-center gap-1 text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      Permission granted
                    </p>
                    {formatOtcMedications(medicalInfo?.otcMedicationsApproved) && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Approved: {formatOtcMedications(medicalInfo.otcMedicationsApproved)}
                      </p>
                    )}
                  </div>
                ) : medicalInfo?.otcMedicationPermission === false ? (
                  <p className="font-medium flex items-center gap-1 text-amber-700">
                    <XCircle className="h-4 w-4" />
                    No permission - contact parent first
                  </p>
                ) : (
                  <p className="text-muted-foreground italic">Not specified</p>
                )}
              </div>

              {/* Special Needs */}
              {medicalInfo?.specialNeeds && (
                <div>
                  <p className="text-sm text-muted-foreground">Special Needs / Disabilities</p>
                  <p className="font-medium">{medicalInfo.specialNeeds}</p>
                </div>
              )}

              {/* Insurance Information */}
              {(medicalInfo?.insuranceInfo?.carrier ||
                medicalInfo?.insuranceInfo?.groupNumber ||
                medicalInfo?.insuranceInfo?.policyNumber ||
                medicalInfo?.insuranceInfo?.policyHolderName) && (
                <div>
                  <p className="text-sm text-muted-foreground">Insurance Information</p>
                  <div className="space-y-1">
                    {medicalInfo?.insuranceInfo?.carrier && (
                      <p className="font-medium">Carrier: {medicalInfo.insuranceInfo.carrier}</p>
                    )}
                    {medicalInfo?.insuranceInfo?.groupNumber && (
                      <p className="font-medium">Group #: {medicalInfo.insuranceInfo.groupNumber}</p>
                    )}
                    {medicalInfo?.insuranceInfo?.policyNumber && (
                      <p className="font-medium">Policy #: {medicalInfo.insuranceInfo.policyNumber}</p>
                    )}
                    {medicalInfo?.insuranceInfo?.policyHolderName && (
                      <p className="font-medium">Policy Holder: {medicalInfo.insuranceInfo.policyHolderName}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground italic">
              No medical information on file.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Admin Notes Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Admin Notes
          </CardTitle>
          {studentProfile && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditDialogMode('admin');
                setIsEditDialogOpen(true);
              }}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {hasAdminNotes ? (
            <p className="whitespace-pre-wrap">{adminMetadata.adminNotes}</p>
          ) : (
            <p className="text-muted-foreground italic">
              No admin notes on file.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Admin Flags Card (only show if there are flags) */}
      {hasAdminFlags && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Admin Flags
            </CardTitle>
            {studentProfile && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditDialogMode('admin');
                  setIsEditDialogOpen(true);
                }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {adminMetadata?.academicProbation && (
                <Badge variant="destructive">Academic Probation</Badge>
              )}
              {adminMetadata?.behavioralProbation && (
                <Badge variant="destructive">Behavioral Probation</Badge>
              )}
              {adminMetadata?.pauseAutoReenroll && (
                <Badge variant="secondary">Auto Re-enroll Paused</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Family Link Card */}
      {family && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Family
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <p className="font-medium">{family.name}</p>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/app/${schoolSlug}/families/${family.slug}`}>
                  View Family
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      {studentProfile && (
        <EditStudentProfileDialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            setIsEditDialogOpen(open);
            if (!open) {
              setEditDialogMode('all'); // Reset mode when dialog closes
            }
          }}
          studentProfile={studentProfile}
          onSuccess={onRefetch}
          showMedicalInfo={editDialogMode === 'medical' || editDialogMode === 'all'}
          showAdminNotes={editDialogMode === 'admin' || editDialogMode === 'all'}
        />
      )}
    </div>
  );
}

export default StudentGeneralTab;
